import type { Language, Address, Document } from "@src/packages/types/common";

export type Insurance = "aetna" | "kaiser" | "bluecross";

export const CMS_EMPLOYEE_BENEFITS = [
  "Carrot",
  "Future Family",
  "Maven",
  "Progyny",
  "Stork Club",
  "United Healthcare",
  "WINFertility",
] as const;

export type EmployeeBenefit = (typeof CMS_EMPLOYEE_BENEFITS)[number];

export const employeeBenefitOptions: { id: EmployeeBenefit; label: string }[] =
  CMS_EMPLOYEE_BENEFITS.map((benefit) => ({
    id: benefit,
    label: benefit,
  }));

export const employeeBenefitIcons: Record<EmployeeBenefit, string> =
  CMS_EMPLOYEE_BENEFITS.reduce(
    (acc, benefit) => {
      acc[benefit] = "👥";
      return acc;
    },
    {} as Record<EmployeeBenefit, string>,
  );

export const CMS_FINANCING_OPTIONS = [
  "CapexMD",
  "Discount pricing",
  "Gaia",
  "Loans",
  "Military discount",
  "Package pricing",
  "PatientFi",
  "Payment plans",
] as const;

export type Financier = (typeof CMS_FINANCING_OPTIONS)[number];

export const financierOptions: { id: Financier; label: string }[] =
  CMS_FINANCING_OPTIONS.map((financier) => ({
    id: financier,
    label: financier,
  }));

export const financierIcons: Record<Financier, string> =
  CMS_FINANCING_OPTIONS.reduce(
    (acc, financier) => {
      acc[financier] = "👥";
      return acc;
    },
    {} as Record<Financier, string>,
  );

export const CMS_TREATMENT_TYPES = [
  "IVF & ICSI",
  "IUI",
  "Egg Freezing",
  "Embryo Freezing",
  "PGT",
  "Ovulation Induction",
  "Sperm Freezing",
  "Donor programs",
  "Surrogacy",
  "Tubal / vasectomy reversal",
  "PGS",
  "Egg freezing",
  "Sperm freezing",
  "PGD",
  "Fertility preservation",
  "Male factor",
  "Endometriosis",
  "Ovulatory dysfunction",
  "Recurrent pregnancy loss",
  "Tubal Reversal",
  "MiniIVF",
  "PRP",
  "Platelet Rich Plasma Therapy",
] as const;

export type Treatment = (typeof CMS_TREATMENT_TYPES)[number];

export const treatmentOptions: { id: Treatment; label: string }[] =
  CMS_TREATMENT_TYPES.map((treatment) => ({
    id: treatment,
    label: treatment,
  }));

export const treatmentIcons: Record<Treatment, string> = {
  ...CMS_TREATMENT_TYPES.reduce(
    (acc, treatment) => {
      acc[treatment] = "👥";
      return acc;
    },
    {} as Record<Treatment, string>,
  ),
  Surrogacy: "🤰",
};

export const CMS_SUPPORTING_SERVICES = [
  "Acupuncture",
  "Mind/Body",
  "Massage therapy",
  "Nutrition counseling",
  "Exercise guidance",
  "Counseling",
  "Support groups",
  "Concierge service",
  "Outside monitoring",
  "Wellness program",
  "Support resources",
  "Patient portal",
  "Yoga",
  "Support Groups",
  "Patient education",
] as const;

export type Service = (typeof CMS_SUPPORTING_SERVICES)[number];

export const serviceOptions: { id: Service; label: string }[] =
  CMS_SUPPORTING_SERVICES.map((service) => ({
    id: service,
    label: service,
  }));

export const serviceIcons: Record<Service, string> = {
  ...CMS_SUPPORTING_SERVICES.reduce(
    (acc, service) => {
      acc[service] = "👥";
      return acc;
    },
    {} as Record<Service, string>,
  ),
  "Massage therapy": "💆",
  "Concierge service": "🛎️",
  "Outside monitoring": "📊",
  "Wellness program": "🧘",
  "Support resources": "📚",
  "Patient portal": "💻",
};

export const CMS_CLINIC_TYPES = [
  "Boutique",
  "Hospital-based",
  "Network & group",
] as const;

export type ClinicType = (typeof CMS_CLINIC_TYPES)[number];

export const clinicTypeOptions: { id: ClinicType; label: string }[] =
  CMS_CLINIC_TYPES.map((type) => ({
    id: type,
    label: type,
  }));

export const clinicTypeIcons: Record<ClinicType, string> =
  CMS_CLINIC_TYPES.reduce(
    (acc, type) => {
      acc[type] = "👥";
      return acc;
    },
    {} as Record<ClinicType, string>,
  );

export const CMS_ACCESSIBILITY = [
  "Wheelchair access",
  "Elevator access",
  "Sign language interpretation",
] as const;

export type Accessibility = (typeof CMS_ACCESSIBILITY)[number];

export const accessibilityOptions: {
  id: Accessibility;
  label: string;
}[] = CMS_ACCESSIBILITY.map((accessibility) => ({
  id: accessibility,
  label: accessibility,
}));

export const accessibilityIcons: Record<Accessibility, string> = {
  ...CMS_ACCESSIBILITY.reduce(
    (acc, accessibility) => {
      acc[accessibility] = "👥";
      return acc;
    },
    {} as Record<Accessibility, string>,
  ),
  "Wheelchair access": "♿",
  "Sign language interpretation": "👋",
};

export const CMS_CONDITIONS = [
  "Infertility",
  "Male Factor",
  "Endometriosis",
  "Reproductive disorders",
  "Diminished ovarian reserve",
  "Male factor",
  "Ovulatory dysfunction",
  "Recurrent pregnancy loss",
  "Tubal factor",
  "Unexplained factor",
  "Uterine factor",
  "Unexplained infertility",
  "PCOS",
  "Uterine fibroids",
  "Congenital anomalies",
  "Irregular periods",
  "Reproductive surgery indications",
  "Uterine Fibroids",
  "Recurrent Pregnancy Loss",
  "Diminished Ovarian Reserve",
  "Tubal blockage",
  "Poor Egg Quality",
  "Anovulation",
  "Unexplained",
  "Poor egg quality",
  "Premature ovarian failure",
  "Male factor infertility",
  "Poor ovarian reserve",
  "Falling fertility preservation needs",
  "Mild male factor infertility",
  "Cervical factor",
  "Mild endometriosis",
  "Structural infertility",
  "Fertility preservation",
  "Oncofertility",
  "Reproductive endocrinology",
  "Hormonal disorders",
  "Abnormal uterine bleeding",
  "Uterine polyps",
  "Irregular menstrual cycles",
  "Pelvic adhesions",
  "Genetic diseases",
] as const;

export type Condition = (typeof CMS_CONDITIONS)[number];

export const conditionOptions: { id: Condition; label: string }[] =
  CMS_CONDITIONS.map((condition) => ({
    id: condition,
    label: condition === "PCOS" ? "PMOS (ex PCOS)" : condition,
  }));

export const conditionIcons: Record<Condition, string> = {
  ...CMS_CONDITIONS.reduce(
    (acc, condition) => {
      acc[condition] = "👥";
      return acc;
    },
    {} as Record<Condition, string>,
  ),
  Oncofertility: "🎗️",
};

export const CMS_PATIENT_GROUPS = [
  "LGBTQIA+",
  "Single Parents",
  "Same sex couples",
  "Gay men",
  "Single parents",
  "LGBTQ+ couples",
] as const;

export type PatientGroup = (typeof CMS_PATIENT_GROUPS)[number];

export const patientGroupOptions: { id: PatientGroup; label: string }[] =
  CMS_PATIENT_GROUPS.map((group) => ({
    id: group,
    label: group,
  }));

export const patientGroupIcons: Record<PatientGroup, string> =
  CMS_PATIENT_GROUPS.reduce(
    (acc, group) => {
      acc[group] = "👥";
      return acc;
    },
    {} as Record<PatientGroup, string>,
  );

export const CMS_CONSULTATION_TYPES = ["In person", "Telehealth"] as const;

export type ConsultationType = (typeof CMS_CONSULTATION_TYPES)[number];

export const consultationTypeOptions: {
  id: ConsultationType;
  label: string;
}[] = CMS_CONSULTATION_TYPES.map((type) => ({
  id: type,
  label: type,
}));

export const consultationTypeIcons: Record<ConsultationType, string> =
  CMS_CONSULTATION_TYPES.reduce(
    (acc, type) => {
      acc[type] = "👥";
      return acc;
    },
    {} as Record<ConsultationType, string>,
  );

export type ClinicTeamMember = {
  name: string;
  title: string;
  role: string;
  imageUrl?: string;
};

export type ClinicDayHours = {
  open: string; // "07:30"
  close: string; // "16:00"
};

export type ClinicSchedule = {
  mon?: ClinicDayHours | null;
  tue?: ClinicDayHours | null;
  wed?: ClinicDayHours | null;
  thu?: ClinicDayHours | null;
  fri?: ClinicDayHours | null;
  sat?: ClinicDayHours | null;
  sun?: ClinicDayHours | null;
  utcOffset?: string | null;
};

export type ClinicLocation = {
  latitude: number;
  longitude: number;
};

export type ClinicSuccessRates = {
  under35?: number;
  between35And37?: number;
  between38And40?: number;
  between41And42?: number;
  over42?: number;
};

export type DonorSuccessRates = {
  freshEmbryosFreshEggs?: number;
  freshEmbryosFrozenEggs?: number;
  frozenEmbryos?: number;
  donatedEmbryos?: number;
};

export type OutcomeSourceType =
  | "national-mandatory-registry"
  | "official-national-publication"
  | "clinic-self-published"
  | "registry-participation"
  | "public-funding-program";

export type Clinic = {
  id: string;
  name: string;
  address: Address;
  headline: string;
  images: Document[];
  tags?: string[] | null;
  services: Service[];
  languages: Language[];
  clinicType: ClinicType;
  conditions: Condition[];
  treatments: Treatment[];
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  bookingUrl?: string | null;
  location?: ClinicLocation | null;
  // insurance_name is free text in CMS (not an enum) - filtering disabled
  insurances?: Insurance[];
  insuranceAccepted?: boolean;
  insuranceName?: string | null;
  insuranceProviders?: string[];
  financiers?: Financier[];
  consultationTypes: ConsultationType[];
  patientGroups: PatientGroup[];
  accessibilities: Accessibility[];
  employeeBenefits?: EmployeeBenefit[];
  schedule?: ClinicSchedule;
  isFirstConsultationFree?: boolean;
  hasReportedAnnualIvfData?: boolean;
  nbCyclePerYear?: number;
  reportedTo?: string | null;
  outcomeSourceType?: OutcomeSourceType;
  /** Direct source page or dataset endpoint for the imported outcome cells. */
  outcomeSourceUrl?: string | null;
  /** Normalized endpoint for the primary imported outcome set. */
  outcomeMetric?:
    | "live_birth_per_transfer"
    | "live_birth_per_retrieval"
    | "live_birth_per_treatment_attempt"
    | "unknown";
  outcomeReportingPopulation?: string | null;
  outcomeDenominatorDescription?: string | null;
  lastReportYear?: number;
  publicNotes?: string | null;
  lastUpdatedIso?: string | null;
  successRates?: ClinicSuccessRates;
  /** Explicit numerator/denominator counts when the source publishes them. */
  successRateDenominators?: ClinicSuccessRates;
  newPatientSuccessRates?: ClinicSuccessRates;
  newPatientSuccessRateDenominators?: ClinicSuccessRates;
  donorSuccessRates?: DonorSuccessRates;
  donorSuccessRateDenominators?: DonorSuccessRates;
  team?: ClinicTeamMember[];
  hasOnSiteLab?: boolean;
  establishedYear?: number;
  isRecordComplete?: boolean;
};

export type ClinicsFilters = {
  search?: string;
  city?: string;
  country?: string;
  userLocation?: ClinicLocation; // User's location for distance-based filtering
  maxDistance?: number; // Maximum distance in miles
  services?: Service[];
  languages?: Language[];
  clinicType?: ClinicType;
  conditions?: Condition[];
  treatments?: Treatment[];
  // insurances?: Insurance[]; // Disabled: CMS insurance_name is free text
  financiers?: Financier[];
  consultationTypes?: ConsultationType[];
  patientGroups?: PatientGroup[];
  accessibilities?: Accessibility[];
  employeeBenefits?: EmployeeBenefit[];
  isFirstConsultationFree?: boolean;
  hasReportedAnnualIvfData?: boolean;
  insuranceAccepted?: boolean;
};

/**
 * User's interaction with a specific clinic
 *
 * Persisted via dashboard API: PUT /me/clinic-interactions/{clinicId}
 */
export type UserClinicInteraction = {
  id: string;
  userId: string;
  clinicId: string;
  /** Whether the user has favorited this clinic */
  isLiked: boolean;
  /** Private notes visible only to the user (max 500 chars recommended) */
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
};
